import {
  ENTITY_TABLES,
  entityClientFields,
  entitySchemas,
  newId,
  type EntityName,
  type Role,
  type TenantSettings,
} from '@dairy/shared';
import type { Table } from 'dexie';
import { db, type OutboxEntry } from './db';

/**
 * S1-T12 — the ONLY way components write data. Each call runs one Dexie transaction that writes or removes the
 * local row AND appends the outbox entry, so a save is never half done (PL-8, NF-10).
 */

export type Change =
  | { entity: EntityName; op: 'upsert'; record: Record<string, unknown> & { id?: string } }
  | { entity: EntityName; op: 'delete'; id: string };

type Listener = () => void;
const listeners = new Set<Listener>();
let currentUserId: string | null = null;

export function setRepoUser(userId: string | null): void {
  currentUserId = userId;
}

/** The sync engine listens to schedule a push 2 s after a save. */
export function onSaved(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export class RepoValidationError extends Error {
  constructor(
    readonly entity: EntityName,
    readonly issues: Array<{ path: string; message: string }>,
  ) {
    super(`Invalid ${entity}: ${issues.map((i) => `${i.path} ${i.message}`).join('; ')}`);
    this.name = 'RepoValidationError';
  }
}

function tableOf(entity: EntityName): Table<Record<string, unknown>, string> {
  return db.table(ENTITY_TABLES[entity].local);
}

/** The client fields of a record (what is pushed). */
export function clientData(entity: EntityName, record: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of entityClientFields[entity]) if (record[k] !== undefined) out[k] = record[k];
  return out;
}

/** Saves several changes in ONE local transaction, in this order (the server receives them in order). */
export async function saveMany(changes: Change[]): Promise<string[]> {
  if (changes.length === 0) return [];
  const userId = currentUserId;
  if (!userId) throw new Error('No signed-in user');
  const tables = [...new Set(changes.map((c) => tableOf(c.entity)))];
  const ids: string[] = [];
  const timeMs = Date.now();
  await db.transaction('rw', [...tables, db.outbox], async () => {
    for (const [i, c] of changes.entries()) {
      const table = tableOf(c.entity);
      // Distinct, increasing times so records saved together keep their order.
      const nowIso = new Date(timeMs + i).toISOString();
      if (c.op === 'upsert') {
        const id = c.record.id ?? newId();
        const existing = await table.get(id);
        const record: Record<string, unknown> = {
          ...c.record,
          id,
          clientCreatedAt: (existing?.clientCreatedAt as string | undefined) ?? (c.record.clientCreatedAt as string | undefined) ?? nowIso,
          createdBy: (existing?.createdBy as string | undefined) ?? userId,
          updatedBy: userId,
          ...(existing?.createdAt ? { createdAt: existing.createdAt } : {}),
          deletedAt: null,
        };
        const data = clientData(c.entity, record);
        const parsed = entitySchemas[c.entity].safeParse(data);
        if (!parsed.success) {
          throw new RepoValidationError(
            c.entity,
            parsed.error.issues.map((x) => ({ path: x.path.join('.'), message: x.message })),
          );
        }
        await table.put(record);
        await supersedeRejected(c.entity, id);
        await db.outbox.add({ mutationId: newId(), entity: c.entity, op: 'upsert', recordId: id, data, clientTs: nowIso, status: 'pending' });
        ids.push(id);
      } else {
        await table.delete(c.id);
        await supersedeRejected(c.entity, c.id);
        await db.outbox.add({ mutationId: newId(), entity: c.entity, op: 'delete', recordId: c.id, clientTs: nowIso, status: 'pending' });
        ids.push(c.id);
      }
    }
  });
  for (const l of listeners) l();
  return ids;
}

/** PL-9 "Edit and retry": a new save of the record replaces its rejected change. */
async function supersedeRejected(entity: EntityName, recordId: string): Promise<void> {
  const rejected = await db.outbox.where('recordId').equals(recordId).filter((o) => o.status === 'rejected' && o.entity === entity).toArray();
  if (rejected.length) await db.outbox.bulkDelete(rejected.map((o) => o.seq!));
}

export function save(entity: EntityName, record: Record<string, unknown> & { id?: string }): Promise<string> {
  return saveMany([{ entity, op: 'upsert', record }]).then((ids) => ids[0]!);
}

export function remove(entity: EntityName, id: string): Promise<string> {
  return saveMany([{ entity, op: 'delete', id }]).then((ids) => ids[0]!);
}

/**
 * PL-13: a photo is a FileAsset record (synced through the outbox) plus a device-only blob (uploaded after the
 * record reaches the server). Both are written in the same transaction as the other changes.
 */
export async function savePhotoWith(
  photo: { id: string; kind: 'ANIMAL_PHOTO' | 'RECEIPT'; blob: Blob },
  changes: Change[],
): Promise<string[]> {
  const asset: Change = {
    entity: 'fileAsset',
    op: 'upsert',
    record: { id: photo.id, kind: photo.kind, mimeType: photo.blob.type || 'image/jpeg', sizeBytes: photo.blob.size },
  };
  let ids: string[] = [];
  await db.transaction('rw', [db.fileBlobs, db.fileAssets, db.outbox, ...new Set(changes.map((c) => tableOf(c.entity)))], async () => {
    await db.fileBlobs.put({ id: photo.id, blob: photo.blob, status: 'pending' });
    ids = await saveMany([asset, ...changes]);
  });
  return ids;
}

/**
 * PL-9 "Discard": drop a rejected change and restore the server's version of the record
 * (`server` = null when the server has none, which removes the local row).
 */
export async function discardRejected(entry: OutboxEntry, server: Record<string, unknown> | null): Promise<void> {
  const table = tableOf(entry.entity);
  await db.transaction('rw', [table, db.outbox], async () => {
    await db.outbox.delete(entry.seq!);
    if (server) await table.put(server);
    else await table.delete(entry.recordId);
  });
  for (const l of listeners) l();
}

/** Keep a downloaded photo on the device (S4-T11 "cached"). */
export async function cachePhoto(id: string, blob: Blob): Promise<void> {
  await db.fileBlobs.put({ id, blob, status: 'cached' });
}

/** Has the record a change waiting to be pushed (or rejected)? */
export async function isPending(recordId: string): Promise<boolean> {
  return (await db.outbox.where('recordId').equals(recordId).count()) > 0;
}

/**
 * PL-11: a MANAGER may edit or delete a record only within the edit window of its first save
 * (server createdAt, or clientCreatedAt when not yet synced).
 */
export function isLocked(
  record: { createdAt?: string; clientCreatedAt?: string } | null | undefined,
  user: { role: Role } | null | undefined,
  settings: Pick<TenantSettings, 'managerEditWindowHours'>,
  nowMs: number = Date.now(),
): boolean {
  if (!record || !user || user.role === 'OWNER') return false;
  const first = record.createdAt ?? record.clientCreatedAt;
  if (!first) return false;
  return nowMs - Date.parse(first) > settings.managerEditWindowHours * 3_600_000;
}

export type { OutboxEntry };
